import { ChangeEvent, FC, useLayoutEffect, useState } from "react";
import classNames from "classnames";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from 'use-debounce';
import OutsideClickHandler from "../OutsideClickHandler/OutsideClickHandler";

import { AddBrandProps } from "./types";
import { Button } from "..";
import { FormikInputField } from "../FormikField";
import RelatedBrands from "../RelatedBrands/RelatedBrands";
import { fetchRelatedBrands, resetRelatedBrands } from "../../store/auth/slice";
import { LogoResponse } from "../../store/brands/types";
import { selectAuthCompany } from "../../store/selectors/auth";
import { addBrandSchema } from "../../utils/validators";
import analytics from "../../lib/Analytics";
import { Company } from "../../store/auth/types";
import { LOGOS } from "./constants";

import styles from "./AddBrand.module.css";



const AddBrand: FC<AddBrandProps> = ({ 
  classNameContainer,
  classNameHeader,
  classNameTitle,
  classNameCaption,
  isShowMonitored = true, 
  onSubmit 
}) => {
  const dispatch = useDispatch();
  const company = useSelector(selectAuthCompany);
  const [brandSelected, setBrandSelected] = useState<Company | null>(null);
  const [isRelatedBrandsOpen, setIsRelatedBrandsOpen] = useState(false);

  useLayoutEffect(() => {
    if (company) {
      setBrandSelected(company);
    }
  }, [company]);

  const debouncedFetch = useDebouncedCallback((value: string) => {
    if (value) {
      dispatch(fetchRelatedBrands({ brand: value }));
      setIsRelatedBrandsOpen(true);
    } else {
      dispatch(resetRelatedBrands());
      setIsRelatedBrandsOpen(false);
    }
  }, 300);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!value.trim()) {
      return;
    }

    setBrandSelected({
      name: value
    });

    debouncedFetch(value);
  }

  const handleOnSubmit = () => {
    if (brandSelected) {
      onSubmit(brandSelected);
      
      analytics.track('Input brand name', {
        location: 'https://app.aicarma.com/',
        brand: brandSelected.name
      });
    }
  }

  const handleOnOutsideClick = () => {
    setIsRelatedBrandsOpen(false);
  }

  const handleOnSelect = (setFieldValue: (field: string, value: string) => void) => {
    return (brandSelected: LogoResponse) => {
      setBrandSelected({
        name: brandSelected.name,
        logoUrl: brandSelected.logo_url,
        website: brandSelected.domain,
      });
      setFieldValue('brand', brandSelected.name);
      dispatch(resetRelatedBrands());
    }
  }

  return (
    <section className={classNames(styles.brand, classNameContainer)}>
      <header className={classNames(styles.brand__header, classNameHeader)}>
        <p className={styles.brand__label}>Your Business</p>
        <h1 className={classNames(styles.brand__title, classNameTitle)}>Drop your brand's name and snag a killer list of your competitors</h1>
        <p className={classNames(styles.brand__caption, classNameCaption)}>We're gonna fire up a bunch of LLMs for scoring analysis right away, so go ahead and vibe with it!</p>
      </header>

      <Formik
        enableReinitialize
        initialValues={{ brand: company?.name ?? "" }}
        validationSchema={addBrandSchema}
        onSubmit={handleOnSubmit}
      >
        {({ setFieldValue }) => (
          <Form className={classNames(styles.brand__form, styles.form)}>
            <div className={classNames(styles.form__item, styles.form__item_related)}>
              <FormikInputField
                autoComplete="off"
                className={styles.form__input}
                name="brand"
                placeholder="Website, company name or product..."
                onFocus={handleInput}
                onInput={handleInput}
              />
              {isRelatedBrandsOpen &&
                <OutsideClickHandler onOutsideClick={handleOnOutsideClick}>
                  <RelatedBrands 
                    className={styles.form__related} 
                    onSelect={handleOnSelect(setFieldValue)} />
                </OutsideClickHandler>
              }
            </div>

            <Button
              typeStyle="fill-auth"
              className={styles.form__button}
              type="submit">
              Continue
            </Button>
          </Form>
        )}
      </Formik>

      {isShowMonitored && (
        <div className={styles.monitored}>
          <h2 className={styles.monitored__title}>2,000+ Brands already monitored</h2>
          <div className={styles.monitored__list}>
            {LOGOS.map((logo) => (
              <img src={logo.logo_url} alt={logo.name} key={logo.name} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default AddBrand;
